import React, { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Flex, Row, Col, AutoComplete, Spin, Input } from "antd";
import { SearchOutlined, EnvironmentOutlined, AimOutlined } from "@ant-design/icons";
import Footer from "./snipets/Footer";
import TopNavigation from "./snipets/TopNavigation";
import phone  from "../assets/mobile-app-on-phone.png";
import google from "../assets/GetItOnGooglePlay_Badge_Web_color_English.svg";
import apple  from "../assets/Download_on_the_App_Store_Badge_NL_RGB_blk.svg";
import bg     from "../assets/1000221189.png";

import food_location_logo from "../assets/food-delivery-location.png";
import burger_logo from "../assets/hamburger.png";
import food_order_logo from "../assets/food-delivery.png";

import { LocationService } from "../services/LocationService";
import { MyLocation } from "../models/Location";

const { Title, Text, Paragraph } = Typography;

const BRAND       = "#00AF66";
const BRAND_LIGHT = "#E6F7EF";
const CURRENT_LOCATION_KEY = "__current_location__";

// ── Landing Page ──────────────────────────────────────────────────────────────

const LandingPage: React.FC = () => {
    return (
        <>
            <TopNavigation />

            {/* Hero */}
            <div style={{ background: "#f5f5f0", marginTop: 64 }}>
                <Row>
                    <Col xs={24} md={12}>
                        <Flex
                            vertical
                            justify="center"
                            style={{ padding: "clamp(32px, 6vw, 64px) clamp(20px, 4vw, 48px)", height: "100%" }}
                            gap="large"
                        >
                            <div>
                                <Title style={{ fontSize: "clamp(26px, 4vw, 40px)", marginBottom: 8 }}>
                                    Order food and more
                                </Title>
                                <Paragraph type="secondary" style={{ fontSize: "clamp(15px, 2vw, 18px)" }}>
                                    Restaurants and grocery stores delivering near you
                                </Paragraph>
                            </div>
                            <AddressSearch />
                        </Flex>
                    </Col>
                    <Col xs={0} md={12}>
                        <img src={bg} alt="hero" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                    </Col>
                </Row>
            </div>

            <HowToOrderSection />
            <DownloadAppSection />
            <Footer />
        </>
    );
};

// ── Address Search Dropdown ────────────────────────────────────────────────

const AddressSearch: React.FC = () => {
    const navigate = useNavigate();

    const [query, setQuery]     = useState("");
    const [results, setResults] = useState<MyLocation[]>([]);
    const [loading, setLoading] = useState(false);
    const [locating] = useState(false);

    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const runSearch = useCallback((text: string) => {
        LocationService.getAddressesByKeyword(text)
            .then((data) => setResults(data))
            .catch(() => setResults([]))
            .finally(() => setLoading(false));
    }, []);

    const handleChangeText = (text: string) => {
        setQuery(text);

        if (debounceRef.current) clearTimeout(debounceRef.current);

        if (!text || text.length < 2) {
            setResults([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        debounceRef.current = setTimeout(() => runSearch(text), 400);
    };

    const buildFullAddress = (location: MyLocation) =>
        [
            location.address?.unit,
            location.address?.street,
            location.address?.suburb,
            location.address?.town,
            location.address?.province,
        ].filter(Boolean).join(", ");

    const goToRestaurants = (latitude: number | string, longitude: number | string) => {
        navigate(`/restaurantsPage/${latitude}/${longitude}`);
    };

    const handleSelectLocation = (location: MyLocation) => {
        location.fullAddress = buildFullAddress(location);
        setQuery(location.fullAddress);
        goToRestaurants(location.latitude, location.longitude);
    };

    const handleUseCurrentLocation = () => {
        /*if (!("geolocation" in navigator)) return;

        setLocating(true);
        navigator.geolocation.getCurrentPosition(
            async (pos) => {
                const { latitude, longitude } = pos.coords;
                try {
                    const location = await LocationService.getAddressByCoords?.(latitude, longitude);
                    const myLocation = location ?? new MyLocation();
                    if (!location) {
                        myLocation.latitude    = latitude.toString();
                        myLocation.longitude   = longitude.toString();
                        myLocation.fullAddress = `${latitude.toFixed(5)}, ${longitude.toFixed(5)}`;
                    }
                    setQuery(myLocation.fullAddress);
                    goToRestaurants(latitude, longitude);
                } finally {
                    setLocating(false);
                }
            },
            () => setLocating(false),
            { enableHighAccuracy: true, timeout: 8000 }
        );*/
    };

    const options = [
        {
            value: CURRENT_LOCATION_KEY,
            label: (
                <Flex align="center" gap={10} style={{ padding: "6px 4px" }}>
                    <div style={optionIconStyle}>
                        {locating ? <Spin size="small" /> : <AimOutlined style={{ color: BRAND, fontSize: 16 }} />}
                    </div>
                    <div>
                        <Text strong style={{ color: BRAND }}>
                            {locating ? "Finding your location…" : "Use my current location"}
                        </Text>
                        <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>Allow location access</Text>
                        </div>
                    </div>
                </Flex>
            ),
        },
        ...(results.length > 0
            ? [{
                value: "__label__",
                label: (
                    <Text type="secondary" style={{ fontSize: 11, letterSpacing: 0.8, fontWeight: 700 }}>
                        SEARCH RESULTS
                    </Text>
                ),
                disabled: true,
            }]
            : []),
        ...results.map((item) => ({
            value: String(item.id),
            label: (
                <Flex align="center" gap={10} style={{ padding: "6px 4px" }}>
                    <div style={optionIconStyle}>
                        <EnvironmentOutlined style={{ color: BRAND, fontSize: 16 }} />
                    </div>
                    <div>
                        <Text>{[item.address.unit, item.address.street].filter(Boolean).join(" ")}</Text>
                        <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {item.address.suburb} {item.address.town} {item.address.postal_code}
                            </Text>
                        </div>
                    </div>
                </Flex>
            ),
            location: item,
        })),
    ];

    return (
        <AutoComplete
            style={{ maxWidth: 460, width: "100%" }}
            options={options}
            value={query}
            onChange={handleChangeText}
            onSelect={(value) => {
                if (value === CURRENT_LOCATION_KEY) {
                    handleUseCurrentLocation();
                    return;
                }
                const option = options.find((o) => o.value === value) as any;
                if (option?.location) handleSelectLocation(option.location);
            }}
            notFoundContent={
                query.length >= 2 && !loading ? (
                    <Flex vertical align="center" style={{ padding: "24px 0" }} gap={4}>
                        <EnvironmentOutlined style={{ color: BRAND, fontSize: 28 }} />
                        <Text type="secondary">No results found</Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Try a different street or area name
                        </Text>
                    </Flex>
                ) : null
            }
        >
            <Input
                placeholder="Full address"
                size="large"
                prefix={<SearchOutlined style={{ color: "#8F9BB3" }} />}
                suffix={loading ? <Spin size="small" /> : null}
            />
        </AutoComplete>
    );
};

const optionIconStyle: React.CSSProperties = {
    width: 34,
    height: 34,
    borderRadius: 10,
    background: BRAND_LIGHT,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1.5px solid #BFEAD8",
};

// ── How to Order ──────────────────────────────────────────────────────────────

const steps = [
    {
        icon:  <img src={food_location_logo} alt="Hlawulani Leonel Greenwich" style={{ height: 64 }} />,
        title: "Tell us where you are",
        desc:  "We'll show you stores and restaurants nearby that you can order from.",
    },
    {
        icon: <img src={burger_logo} alt="Hlawulani Leonel Greenwich" style={{ height: 64 }} />,
        title: "Find what you want",
        desc:  "Search for items or dishes, businesses or cuisines.",
    },
    {
        icon: <img src={food_order_logo} alt="Hlawulani Leonel Greenwich" style={{ height: 64 }} />,
        title: "Order for delivery or collection",
        desc:  "We'll update you on your order's progress.",
    },
];

const HowToOrderSection: React.FC = () => (
    <div style={{ background: "#fff", padding: "clamp(40px, 7vw, 56px) clamp(16px, 4vw, 24px)" }}>
        <Flex vertical align="center" style={{ marginBottom: 32 }}>
            <Text type="secondary" style={{ textTransform: "uppercase", letterSpacing: 1, fontSize: 15, fontWeight: 600 }}>
                How to order
            </Text>
            <Title level={2} type="success" style={{ margin: 0 }}>It's as easy as this.</Title>
        </Flex>

        <Row gutter={[32, 32]} justify="center">
            {steps.map((step) => (
                <Col xs={24} sm={8} key={step.title} style={{ textAlign: "center" }}>
                    <Flex vertical align="center" gap="middle">
                        {step.icon}
                        <Title style={{ margin: 0, fontSize: 20 }}>{step.title}</Title>
                        <Text type="secondary" style={{ fontSize: 18 }}>
                            {step.desc}
                        </Text>
                    </Flex>
                </Col>
            ))}
        </Row>
    </div>
);

// ── Download App ──────────────────────────────────────────────────────────────

const DownloadAppSection: React.FC = () => (
    // NOTE: vertical padding (56px) is fixed on purpose — the phone image below uses
    // -56px top/bottom margins to bleed past the section, so this value is load-bearing,
    // not decorative. Only horizontal padding scales with viewport.
    <div style={{ background: "#dff0ea", padding: "56px clamp(16px, 4vw, 24px)", overflow: "hidden" }}>
        <Row gutter={[32, 32]} align="middle" justify="center">
            <Col xs={24} md={10}>
                <Title style={{ fontSize: "clamp(24px, 3vw, 34px)", marginBottom: 8 }}>Download the app</Title>
                <Paragraph type="secondary" style={{ fontSize: 16, marginBottom: 20 }}>
                    Click, sit back, and enjoy.
                </Paragraph>
                <Flex gap={12} wrap="wrap" align="center">
                    <img src={google} alt="Google Play"  style={{ height: 52, objectFit: "contain" }} />
                    <img src={apple}  alt="App Store"    style={{ height: 52, objectFit: "contain" }} />
                </Flex>
            </Col>
            <Col xs={0} md={8}>
                <img
                    src={phone}
                    alt="App on phone"
                    style={{ height: 480, width: "auto", objectFit: "contain", display: "block", marginLeft: "auto", marginTop: -56, marginBottom: -56 }}
                />
            </Col>
        </Row>
    </div>
);

export default LandingPage;